"use client";

import { useState } from "react";
import { GripVertical, List, Plus, Trash2 } from "lucide-react";

import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useLocale } from "@/hooks/useLocale";
import { cn } from "@/lib/utils";
import { t } from "@/lib/localization";
import {
    ColumnsBlock,
    ColumnVerticalAlign,
    LayoutColumn,
} from "@/lib/utils/lomdaV3/types";
import { useLomdaV3Editor } from "../../store/useLomdaV3Editor";
import { createLayoutColumn } from "../../lib/blockRegistry";
import {
    columnShares,
    columnWidthRange,
    wholePercents,
    withColumnWidth,
} from "../../lib/columnWidths";
import { COLUMN_ALIGNS } from "../blocks/layout/columnAlign";
import {
    NumberField,
    PopoverTitle,
    WidthControl,
    chromeBtn,
    chromeBtnOpen,
} from "./blockAppearance";

const MIN_COLUMNS = 2;
const MAX_COLUMNS = 4;

/** Widths are re-seeded to an equal split whenever the column count changes. */
const equalWidths = (columns: LayoutColumn[]) =>
    columns.map((c) => ({ ...c, width: undefined }));

export default function ColumnsSettings({ block }: { block: ColumnsBlock }) {
    const replaceBlockData = useLomdaV3Editor((s) => s.replaceBlockData);
    const columns = block.data.columns;
    const setColumns = (next: LayoutColumn[]) =>
        replaceBlockData(block.id, { columns: next });

    const addColumn = () => {
        if (columns.length >= MAX_COLUMNS) return;
        setColumns(equalWidths([...columns, createLayoutColumn()]));
    };
    // Delete merges the removed column's blocks into an adjacent one.
    const deleteColumn = (id: string) => {
        if (columns.length <= MIN_COLUMNS) return;
        const idx = columns.findIndex((c) => c.id === id);
        const removed = columns[idx];
        const next = columns.filter((c) => c.id !== id);
        const mergeInto = idx > 0 ? idx - 1 : 0;
        next[mergeInto] = {
            ...next[mergeInto],
            blocks: [...next[mergeInto].blocks, ...removed.blocks],
        };
        setColumns(equalWidths(next));
    };
    const move = (from: number, to: number) => {
        if (from === to) return;
        const next = [...columns];
        const [item] = next.splice(from, 1);
        next.splice(to, 0, item);
        setColumns(next);
    };
    const setAlign = (id: string, align: ColumnVerticalAlign) =>
        setColumns(
            columns.map((c) =>
                c.id === id
                    ? {
                          ...c,
                          verticalAlign: align === "top" ? undefined : align,
                      }
                    : c
            )
        );
    const setWidth = (index: number, value: number) => {
        const next = withColumnWidth(columns, index, value);
        if (next) setColumns(next);
    };

    return (
        <div className="flex items-center gap-3">
            <span className="shrink-0 text-sm !font-semibold text-foreground">
                {t("lomda-v3-editor.layout.columns")}
            </span>
            <WidthControl blockId={block.id} />
            <ManageColumnsPopover
                columns={columns}
                canDelete={columns.length > MIN_COLUMNS}
                canAdd={columns.length < MAX_COLUMNS}
                onMove={move}
                onDelete={deleteColumn}
                onAdd={addColumn}
                onAlign={setAlign}
                onWidth={setWidth}
            />
        </div>
    );
}

function ManageColumnsPopover({
    columns,
    canDelete,
    canAdd,
    onMove,
    onDelete,
    onAdd,
    onAlign,
    onWidth,
}: {
    columns: LayoutColumn[];
    canDelete: boolean;
    canAdd: boolean;
    onMove: (from: number, to: number) => void;
    onDelete: (id: string) => void;
    onAdd: () => void;
    onAlign: (id: string, align: ColumnVerticalAlign) => void;
    onWidth: (index: number, value: number) => void;
}) {
    const [dragIndex, setDragIndex] = useState<number | null>(null);
    const { dir } = useLocale();
    const widths = wholePercents(columnShares(columns));
    return (
        <Popover>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    className={cn(chromeBtn, chromeBtnOpen)}
                >
                    <List className="size-5" />
                    {t("lomda-v3-editor.text.manage-columns")}
                </button>
            </PopoverTrigger>
            <PopoverContent
                align="end"
                dir={dir}
                // Not into column 1's width box: that would open the
                // on-screen keyboard and take Ctrl+Z from the editor.
                onOpenAutoFocus={(e) => {
                    e.preventDefault();
                    (e.currentTarget as HTMLElement).focus();
                }}
                className="w-[352px] rounded-[20px] border border-border bg-card p-4 shadow-md"
            >
                <div className="flex flex-col gap-4">
                    <PopoverTitle>
                        {t("lomda-v3-editor.text.manage-columns")}
                    </PopoverTitle>
                    {columns.map((col, i) => (
                        <div
                            key={col.id}
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={() => {
                                if (dragIndex !== null) onMove(dragIndex, i);
                                setDragIndex(null);
                            }}
                            className="flex items-center gap-2.5"
                        >
                            <span
                                draggable
                                onDragStart={(e) => {
                                    setDragIndex(i);
                                    const row = e.currentTarget.parentElement;
                                    if (!row) return;
                                    const r = row.getBoundingClientRect();
                                    e.dataTransfer.setDragImage(
                                        row,
                                        e.clientX - r.left,
                                        e.clientY - r.top
                                    );
                                }}
                                onDragEnd={() => setDragIndex(null)}
                                className="shrink-0 cursor-grab"
                            >
                                <GripVertical className="size-5 text-muted-foreground" />
                            </span>
                            <span className="min-w-0 flex-1 text-sm text-foreground">
                                {t("lomda-v3-editor.text.column-n", undefined, {
                                    number: i + 1,
                                })}
                            </span>
                            <NumberField
                                label={t(
                                    "lomda-v3-editor.layout.column-width",
                                    undefined,
                                    { number: i + 1 }
                                )}
                                value={widths[i]}
                                {...columnWidthRange(widths, i)}
                                onChange={(v) => onWidth(i, v)}
                            />
                            <ToggleGroup
                                type="single"
                                size="sm"
                                dir={dir}
                                aria-label={t(
                                    "lomda-v3-editor.text.column-n",
                                    undefined,
                                    { number: i + 1 }
                                )}
                                value={col.verticalAlign ?? "top"}
                                onValueChange={(value) => {
                                    const picked = COLUMN_ALIGNS.find(
                                        (a) => a.value === value
                                    );
                                    if (picked) onAlign(col.id, picked.value);
                                }}
                                className="shrink-0 gap-0.5"
                            >
                                {COLUMN_ALIGNS.map(
                                    ({ value, icon: Icon, labelKey }) => (
                                        <ToggleGroupItem
                                            key={value}
                                            value={value}
                                            aria-label={t(labelKey)}
                                            title={t(labelKey)}
                                            className="size-8 min-w-8 px-0"
                                        >
                                            <Icon />
                                        </ToggleGroupItem>
                                    )
                                )}
                            </ToggleGroup>
                            <button
                                type="button"
                                disabled={!canDelete}
                                onClick={() => onDelete(col.id)}
                                className="shrink-0 text-muted-foreground transition hover:text-destructive disabled:opacity-30"
                            >
                                <Trash2 className="size-5" />
                            </button>
                        </div>
                    ))}
                    <span className="h-px w-full bg-muted" />
                    <button
                        type="button"
                        disabled={!canAdd}
                        onClick={onAdd}
                        className="flex items-center gap-2.5 text-sm text-foreground transition hover:text-primary disabled:pointer-events-none disabled:opacity-30"
                    >
                        <Plus className="size-5 shrink-0" />
                        {t("lomda-v3-editor.text.add-column")}
                    </button>
                </div>
            </PopoverContent>
        </Popover>
    );
}
